import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete } from '@mantine/core';
import axios from 'axios';

import { getCustomerAutocomplete } from '../../api/customers.api';

import type { Customer } from '../../types/customer';
import type { AutocompleteProps } from '@mantine/core';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

// Separator that won't appear in customer name/city/state data.
const ITEM_VALUE_SEPARATOR = '|||';

/** Builds a display label for a customer suggestion, e.g. "Acme — Austin, TX". */
const buildCustomerLabel = (customer: Customer): string => {
  const place = [customer.city, customer.state].filter(Boolean).join(', ');
  return place ? `${customer.name} — ${place}` : customer.name;
};

/**
 * Encodes the display label and customerId into a single Mantine option value.
 * Label comes first so Mantine's default substring filter still matches against
 * what the user typed. The customerId suffix guarantees uniqueness even when two
 * customers share the same name and location.
 */
const buildItemValue = (customer: Customer): string => `${buildCustomerLabel(customer)}${ITEM_VALUE_SEPARATOR}${customer.customerId}`;

const extractLabel = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[0];

const extractCustomerId = (itemValue: string): string => itemValue.split(ITEM_VALUE_SEPARATOR)[1] ?? '';

/**
 * Forwards every Mantine `Autocomplete` prop. `data` and `onOptionSubmit` are
 * managed internally (the component owns the suggestion list and resolves a
 * picked option back to its `Customer`), so they're omitted from the public
 * surface in favor of `onCustomerSelect`.
 */
export type CustomerAutocompleteProps = Omit<AutocompleteProps, 'data' | 'onOptionSubmit'> & {
  countryCodes?: readonly string[];
  isRequired?: boolean | undefined;
  /** Called with the full record when a customer suggestion is picked. */
  onCustomerSelect?: (customer: Customer) => void;
};

/**
 * A self-fetching customer search field built on Mantine's `Autocomplete`.
 *
 * As the user types it fetches suggestions from `/customer/autocomplete` once at
 * least {@link MIN_SEARCH_LENGTH} characters are entered, debounced by
 * {@link SEARCH_DEBOUNCE_MS}. A change while a request is in flight aborts the
 * prior request and issues a new one, so only the latest query's results land.
 */
export const CustomerAutocomplete = ({
  countryCodes,
  isRequired,
  onChange,
  onCustomerSelect,
  value,
  ...autocompleteProps
}: CustomerAutocompleteProps) => {
  const [customers, setCustomers] = useState<Customer[]>([]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Mantine fires onChange with the picked option's label immediately after
  // onOptionSubmit. That trailing change would clobber the consumer's
  // select-driven update (e.g. an address autofill) and overwrite the field with
  // the composite label, so we swallow exactly one onChange after a submit.
  const didJustSubmitRef = useRef(false);

  // Abort any in-flight request and cancel a pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  // Key by customerId so two customers with identical names/locations resolve correctly.
  const customerById = useMemo(() => new Map(customers.map((customer) => [customer.customerId, customer])), [customers]);
  const data = useMemo(() => customers.map(buildItemValue), [customers]);

  const runSearch = useCallback(
    (searchString: string) => {
      // Cancel the request in flight; the latest query wins.
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      getCustomerAutocomplete(searchString, controller.signal)
        .then((results) => {
          if (controller.signal.aborted) return;
          setCustomers(countryCodes ? results.filter((customer) => countryCodes.includes(customer.countryCode)) : results);
        })
        .catch((error) => {
          // Ignore cancellations; clear suggestions on a real failure.
          if (!axios.isCancel(error)) setCustomers([]);
        });
    },
    [countryCodes],
  );

  const handleChange = useCallback(
    (nextValue: string) => {
      // Swallow the trailing onChange Mantine fires right after an option pick.
      if (didJustSubmitRef.current) {
        didJustSubmitRef.current = false;
        return;
      }

      onChange?.(nextValue);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (nextValue.length < MIN_SEARCH_LENGTH) {
        abortControllerRef.current?.abort();
        setCustomers([]);
        return;
      }

      debounceTimerRef.current = setTimeout(() => runSearch(nextValue), SEARCH_DEBOUNCE_MS);
    },
    [onChange, runSearch],
  );

  const handleOptionSubmit = useCallback(
    (itemValue: string) => {
      const customer = customerById.get(extractCustomerId(itemValue));
      if (customer) {
        didJustSubmitRef.current = true;
        onCustomerSelect?.(customer);
      }
    },
    [customerById, onCustomerSelect],
  );

  return (
    <Autocomplete
      {...autocompleteProps}
      data={data}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      renderOption={({ option }) => extractLabel(option.value)}
      required={isRequired}
      value={value}
    />
  );
};
